import { cn } from '@commenda/utils';
import React, { useCallback, useMemo, useState } from 'react';
import { InputErrorMessage } from '../InputErrorMessage';
import { DateSegmentInput } from './components/DateSegmentInput';
import { DateSeparator } from './components/DateSeparator';
import { DISPLAY_FORMAT_CONFIG, SEGMENT_CONFIG } from './constants';
import { useDateKeyboardNavigation } from './hooks/useDateKeyboardNavigation';
import { useDateNavigation } from './hooks/useDateNavigation';
import { useDateSegmentHandlers } from './hooks/useDateSegmentHandlers';
import { useDateSegments } from './hooks/useDateSegments';
import { DateFieldProps, Segment } from './types';

const DateField = React.forwardRef<HTMLDivElement, DateFieldProps>(
  (
    {
      value,
      onChange,
      error,
      showErrorMessage = true,
      disabled = false,
      className,
      onBlur,
      onFocus,
      displayFormat = 'iso',
      disallowFutureDates = false,
      fullWidth = false,
      onValidationError,
      ...props
    },
    ref
  ) => {
    const { segments, setSegments, setIsEditing } = useDateSegments(value);
    const { focusedSegment, setFocusedSegment, refs, blurSegment } =
      useDateNavigation();
    const [isContainerFocused, setIsContainerFocused] = useState(false);

    // Get format configuration
    const formatConfig = useMemo(
      () => DISPLAY_FORMAT_CONFIG[displayFormat],
      [displayFormat]
    );
    const { segmentOrder, separator } = formatConfig;

    const {
      onYearChange,
      onMonthChange,
      onDayChange,
      onMonthBlur,
      onDayBlur,
      onSegmentChange,
      internalError,
    } = useDateSegmentHandlers({
      segments,
      setSegments,
      onChange,
      refs,
      setIsEditing,
      disallowFutureDates,
      segmentOrder,
      onValidationError,
    });

    const { onKeyDown } = useDateKeyboardNavigation({
      segments,
      setSegments,
      refs,
      onSegmentChange,
      setFocusedSegment,
      setIsEditing,
      segmentOrder,
    });

    const onSegmentFocus = useCallback(
      (segment: Segment) => {
        setFocusedSegment(segment);
      },
      [setFocusedSegment]
    );

    const onContainerFocus = useCallback(
      (e: React.FocusEvent<HTMLDivElement>) => {
        setIsContainerFocused(true);
        if (onFocus) {
          onFocus(e);
        }
      },
      [onFocus]
    );

    const onContainerBlur = useCallback(
      (e: React.FocusEvent<HTMLDivElement>) => {
        // Check if focus is moving to another element within the container
        const relatedTarget = e.relatedTarget as HTMLElement | null;
        const container = e.currentTarget;

        // If focus is moving to another element within this container, don't blur
        if (relatedTarget && container.contains(relatedTarget)) {
          return;
        }

        setIsContainerFocused(false);
        blurSegment(); // Clear focused segment when container loses focus
        if (onBlur) {
          onBlur(e);
        }
      },
      [onBlur, blurSegment]
    );

    const onContainerClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        // Only handle clicks on the container itself or separators, not on inputs
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT') {
          // Let the input handle its own focus
          return;
        }

        // Determine which segment to focus based on segment order and what's filled
        for (const segment of segmentOrder) {
          if (!segments[segment] || segments[segment].length === 0) {
            setFocusedSegment(segment);
            refs[segment].current?.focus();
            return;
          }
        }

        // All segments are filled, focus the last one in order
        const lastSegment = segmentOrder[segmentOrder.length - 1];
        setFocusedSegment(lastSegment);
        refs[lastSegment].current?.focus();
      },
      [segments, refs, setFocusedSegment, segmentOrder]
    );

    // Get the change handler for a segment
    const getSegmentChangeHandler = useCallback(
      (segment: Segment) => {
        switch (segment) {
          case 'year':
            return onYearChange;
          case 'month':
            return onMonthChange;
          case 'day':
            return onDayChange;
        }
      },
      [onYearChange, onMonthChange, onDayChange]
    );

    // Get the blur handler for a segment
    const getSegmentBlurHandler = useCallback(
      (segment: Segment) => {
        switch (segment) {
          case 'year':
            return blurSegment;
          case 'month':
            return onMonthBlur;
          case 'day':
            return onDayBlur;
        }
      },
      [blurSegment, onMonthBlur, onDayBlur]
    );

    const hasError = error || internalError;
    const displayError = error || internalError;

    return (
      <div
        className={cn('w-full flex flex-col gap-2', className)}
        ref={ref}
        {...props}
      >
        <div
          className={cn(
            'relative inline-flex box-border items-center gap-xxs rounded-md border h-10 transition-colors overflow-hidden px-2',
            fullWidth ? 'w-full' : 'w-fit',
            'txt-small-regular',
            disabled &&
              'cursor-not-allowed bg-bg-disabled-subtle disabled:border-border-disabled disabled:pointer-events-none',
            hasError
              ? 'border-border-error hover:border-border-error-hover focus-within:border-border-error-hover focus-within:ring-1 focus-within:ring-inset focus-within:ring-border-error-hover'
              : 'border-border-primary hover:border-border-primary-hover focus-within:ring-4 focus-within:ring-[var(--color-bg-brand-hover)] focus-within:border-fg-brand'
          )}
          onClick={onContainerClick}
          onBlur={onContainerBlur}
          onFocus={onContainerFocus}
        >
          {segmentOrder.map((segment, index) => (
            <React.Fragment key={segment}>
              <DateSegmentInput
                segment={segment}
                value={segments[segment]}
                onChange={getSegmentChangeHandler(segment)}
                onKeyDown={(e) => onKeyDown(e, segment)}
                onFocus={() => onSegmentFocus(segment)}
                onBlur={getSegmentBlurHandler(segment)}
                disabled={disabled}
                inputRef={refs[segment]}
                placeholder={SEGMENT_CONFIG[segment].placeholder}
                ariaLabel={SEGMENT_CONFIG[segment].ariaLabel}
                maxLength={SEGMENT_CONFIG[segment].maxLength}
                isFocused={isContainerFocused && focusedSegment === segment}
              />
              {index < segmentOrder.length - 1 && (
                <DateSeparator separator={separator} />
              )}
            </React.Fragment>
          ))}
        </div>
        {displayError && showErrorMessage ? (
          <InputErrorMessage message={displayError} />
        ) : null}
      </div>
    );
  }
);

DateField.displayName = 'DateField';

export { DateField };
